import { useDeleteAPI, useGetAPI, usePostAPI, usePutAPI } from './useAPI';
import { AJAXError, APICallProps, ServerFlag } from '@types';

/* Admin feature-flag management endpoints (EOS). Raw responses are
   normalized at the consumer (useAdminFeatureFlags) via
   normalizeAdminFeatureFlags / normalizeFlagPreviewers because the
   backend payload shapes vary. Flag names used as route params must
   be encodeURIComponent'd at the call site. */

export const useGetAdminFeatureFlags = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/feature' });

	return {
		loadingFlags: isLoading,
		flagsResponse: data as unknown,
		flagsError: error as AJAXError,
		callGetFlags: callAPI as (props?: APICallProps) => Promise<{
			data: unknown;
			error: AJAXError | null;
		} | undefined>
	}
}

export const useCreateAdminFeatureFlag = () => {
	const {
		isLoading,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/feature' });

	return {
		loadingCreateFeatureFlag: isLoading,
		createFeatureFlagError: error as AJAXError,
		callCreateFlag: callAPI
	}
}

export const useDeleteAdminFeatureFlag = () => {
	const {
		isLoading,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/feature/{1}' });

	return {
		loadingDeleteFlag: isLoading,
		deleteFlagError: error as AJAXError,
		callDeleteFlag: callAPI
	}
}

export const useEnableAdminFeatureFlag = () => {
	const {
		isLoading,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/feature/{1}/enable' });

	return {
		loadingEnableFlag: isLoading,
		enableFlagError: error as AJAXError,
		callEnableFlag: callAPI
	}
}

export const useDisableAdminFeatureFlag = () => {
	const {
		isLoading,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/feature/{1}/disable' });

	return {
		loadingDisableFlag: isLoading,
		disableFlagError: error as AJAXError,
		callDisableFlag: callAPI
	}
}

export const useSetAdminFlagPreviewer = () => {
	const {
		isLoading,
		error,
		callAPI
	} = usePutAPI({ endpoint: '/api/admin/feature/{1}/previewers' });

	return {
		loadingSetPreviewer: isLoading,
		setPreviewerError: error as AJAXError,
		callSetPreviewer: callAPI as (props?: APICallProps<{ user_external_id: string; add: boolean }>) => Promise<{
			data: unknown;
			error: AJAXError | null;
		} | undefined>
	}
}

export const useGetAdminFlagPreviewers = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/feature/{1}/list-previewers' });

	return {
		loadingPreviewers: isLoading,
		previewersResponse: data as unknown,
		previewersError: error as AJAXError,
		callGetPreviewers: callAPI as (props?: APICallProps) => Promise<{
			data: unknown;
			error: AJAXError | null;
		} | undefined>
	}
}

export const useGetUserFeatureFlags = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/feature' });

	return {
		loadingFlags: isLoading,
		flagsResponse: data as { data: { flags: ServerFlag[] } },
		flagsError: error as AJAXError,
		callGetFlags: callAPI as (props?: APICallProps) => Promise<{
			data: { data: { flags: ServerFlag[] } };
			error: AJAXError | null;
		} | undefined>
	}
}